import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { User, Car, Settings, MessageSquare, FileCheck } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import ConsentAgreement from "@/components/ConsentAgreement";
import BotProtection from "@/components/BotProtection";
import { isFilled, isEmailFilled, requiredFieldsFilled } from "@/utils/formReady";
import { trackLead } from "@/utils/ga4";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const VEHICLE_YEAR_MAX = new Date().getFullYear() + 1;

const VEHICLE_FEATURES = [
  "4x4", "Adjustable Pedals", "Air Conditioning", "Air Conditioned Seats", "Alarm System", "Alloy Wheels",
  "AM/FM", "Anti-Lock Brakes", "Auto Climate Control", "Back Up Sonar", "Bed Cover", "Bedliner",
  "Bucket Seats", "CD", "CD Changer", "Convertible Top", "Cruise Control", "Diesel",
  "Driver Airbag", "Extra Cab", "Fog Lights", "Heated Seats", "Keyless Entry", "Leather Seats",
  "Luggage Rack", "Memory Seats", "Navigation", "Passenger Airbag", "Power Doors", "Power Locks",
  "Power Mirrors", "Power Seats", "Power Tailgate", "Power Windows", "Quad Seats", "Rear Air",
  "Rear Defroster", "Rear Spoiler", "Running Boards", "Satellite Radio", "Side Airbags", "Sun Roof",
  "Third Row Seat", "Tilt Wheel", "Tinted Windows", "Tool Box", "Tow Package", "Traction Control",
  "TV/DVD", "TV/VCR", "Woodgrain"
];

const TRANSMISSION_TYPES = ["Automatic", "Manual", "CVT", "Semi-Automatic", "Other"];
const CYLINDER_OPTIONS = ["3", "4", "5", "6", "8", "10", "12", "Electric", "Other"];

const SectionHeader = ({ icon: Icon, title, subtitle, isDark }) => (
  <div className="flex items-center gap-4 mb-6">
    <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-blue-600/10' : 'bg-red-600/10'}`}>
      <Icon className={`h-6 w-6 ${isDark ? 'text-blue-500' : 'text-red-600'}`} />
    </div>
    <div>
      <h2 className={`font-unbounded text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
      {subtitle && <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{subtitle}</p>}
    </div>
  </div>
);

const FormField = ({ label, required, children, className = "", isDark }) => (
  <div className={`space-y-2 ${className}`}>
    <Label className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

export default function TradeInPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const inputClass = isDark
    ? "bg-black/50 border-white/10 text-white h-11 rounded-none placeholder:text-gray-600 focus:border-blue-500"
    : "bg-white border-gray-300 text-gray-900 h-11 rounded-none placeholder:text-gray-400 focus:border-red-500";
  const selectTriggerClass = isDark
    ? "bg-black/50 border-white/10 text-white h-11 rounded-none"
    : "bg-white border-gray-300 text-gray-900 h-11 rounded-none";
  const selectContentClass = isDark ? "bg-[#0A0A0A] border-white/10" : "bg-white border-gray-200";
  const selectItemClass = isDark ? "text-white" : "text-gray-900";
  const sectionClass = isDark
    ? "bg-[#0A0A0A] border border-white/10 p-6 md:p-8 mb-6"
    : "bg-white border border-gray-200 shadow-sm p-6 md:p-8 mb-6";

  const getFeatureKey = (feature) => {
    const keyMap = {
      "4x4": "4x4", "Adjustable Pedals": "adjustablePedals", "Air Conditioning": "airConditioning",
      "Air Conditioned Seats": "airConditionedSeats", "Alarm System": "alarmSystem", "Alloy Wheels": "alloyWheels",
      "AM/FM": "amFm", "Anti-Lock Brakes": "antiLockBrakes", "Auto Climate Control": "autoClimateControl",
      "Back Up Sonar": "backUpSonar", "Bed Cover": "bedCover", "Bedliner": "bedliner",
      "Bucket Seats": "bucketSeats", "CD": "cd", "CD Changer": "cdChanger",
      "Convertible Top": "convertibleTop", "Cruise Control": "cruiseControl", "Diesel": "diesel",
      "Driver Airbag": "driverAirbag", "Extra Cab": "extraCab", "Fog Lights": "fogLights",
      "Heated Seats": "heatedSeats", "Keyless Entry": "keylessEntry", "Leather Seats": "leatherSeats",
      "Luggage Rack": "luggageRack", "Memory Seats": "memorySeats", "Navigation": "navigation",
      "Passenger Airbag": "passengerAirbag", "Power Doors": "powerDoors", "Power Locks": "powerLocks",
      "Power Mirrors": "powerMirrors", "Power Seats": "powerSeats", "Power Tailgate": "powerTailgate",
      "Power Windows": "powerWindows", "Quad Seats": "quadSeats", "Rear Air": "rearAir",
      "Rear Defroster": "rearDefroster", "Rear Spoiler": "rearSpoiler", "Running Boards": "runningBoards",
      "Satellite Radio": "satelliteRadio", "Side Airbags": "sideAirbags", "Sun Roof": "sunRoof",
      "Third Row Seat": "thirdRowSeat", "Tilt Wheel": "tiltWheel", "Tinted Windows": "tintedWindows",
      "Tool Box": "toolBox", "Tow Package": "towPackage", "Traction Control": "tractionControl",
      "TV/DVD": "tvDvd", "TV/VCR": "tvVcr", "Woodgrain": "woodgrain",
    };
    return keyMap[feature] || feature;
  };

  const translateFeature = (feature) => {
    const key = getFeatureKey(feature);
    return t(`vehicleFeatures.${key}`) || feature;
  };

  const [personalInfo, setPersonalInfo] = useState({ firstName: "", lastName: "", mobileNumber: "", homeNumber: "", email: "" });
  const [vehicleInfo, setVehicleInfo] = useState({
    vin: "", year: "", make: "", model: "", trim: "", cylinders: "", mileage: "",
    transmission: "", colour: "", financed: "", financedWith: "", payOffAmount: "", accidents: ""
  });
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [vehicleDescription, setVehicleDescription] = useState("");
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fieldsReady =
    isFilled(personalInfo.firstName) &&
    isFilled(personalInfo.lastName) &&
    isEmailFilled(personalInfo.email) &&
    isFilled(personalInfo.mobileNumber) &&
    requiredFieldsFilled([
      vehicleInfo.year,
      vehicleInfo.make,
      vehicleInfo.model,
      vehicleInfo.trim,
      vehicleInfo.cylinders,
      vehicleInfo.mileage,
      vehicleInfo.transmission,
      vehicleInfo.colour,
      vehicleInfo.financed,
      vehicleInfo.accidents,
    ]);
  const canSubmit = consent && fieldsReady;

  const updatePersonalField = useCallback((field, value) => { setPersonalInfo(prev => ({ ...prev, [field]: value })); }, []);
  const updateVehicleField = useCallback((field, value) => { setVehicleInfo(prev => ({ ...prev, [field]: value })); }, []);
  const toggleFeature = useCallback((feature) => {
    setSelectedFeatures(prev => prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(t("consent.formRequired")); return; }
    setSubmitting(true);
    try {
      const payload = {
        personalInfo: { firstName: personalInfo.firstName, lastName: personalInfo.lastName, email: personalInfo.email, mobileNumber: personalInfo.mobileNumber, homeNumber: personalInfo.homeNumber || "" },
        vehicleInfo: { vin: vehicleInfo.vin || "", year: vehicleInfo.year, make: vehicleInfo.make, model: vehicleInfo.model, trim: vehicleInfo.trim, cylinders: vehicleInfo.cylinders, mileage: vehicleInfo.mileage, transmission: vehicleInfo.transmission, colour: vehicleInfo.colour, financed: vehicleInfo.financed, financedWithWhom: vehicleInfo.financedWith || "", payOffAmount: vehicleInfo.payOffAmount || "", accidents: vehicleInfo.accidents },
        vehicleFeatures: selectedFeatures, vehicleDescription: vehicleDescription || "", consent: consent,
        captcha_token: captchaToken,
      };
      await axios.post(`${BACKEND_URL}/api/trade-in`, payload);
      trackLead("trade_in");
      toast.success("Your trade-in information has been submitted successfully! Our team will evaluate your vehicle and contact you within 24-48 hours.");
      setPersonalInfo({ firstName: "", lastName: "", mobileNumber: "", homeNumber: "", email: "" });
      setVehicleInfo({ vin: "", year: "", make: "", model: "", trim: "", cylinders: "", mileage: "", transmission: "", colour: "", financed: "", financedWith: "", payOffAmount: "", accidents: "" });
      setSelectedFeatures([]); setVehicleDescription(""); setConsent(false);
    } catch (error) {
      console.error("Trade-in submission error:", error);
      const detail = error.response?.data?.detail;
      let message = "Failed to submit trade-in information. Please try again.";
      if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI 422 validation errors
        message = detail
          .map((item) => {
            const field = Array.isArray(item?.loc)
              ? item.loc.filter((p) => p !== "body").join(" → ")
              : "";
            const msg = item?.msg || item?.message || JSON.stringify(item);
            return field ? `${field}: ${msg}` : msg;
          })
          .join("; ");
      }
      toast.error(message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`} data-testid="trade-in-page">
      <Navbar />
      
      <div className={`pt-24 pb-12 ${isDark ? 'radial-gradient' : ''}`}>
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className={`text-sm uppercase tracking-widest font-medium mb-3 block ${isDark ? 'text-blue-500' : 'text-red-600'}`}>{t('tradeIn.tagline')}</span>
            <h1 className={`font-unbounded text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('tradeIn.title')}</h1>
            <p className={`text-lg max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('tradeIn.description')}</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <form onSubmit={handleSubmit} data-testid="trade-in-form">
          
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className={sectionClass}>
            <SectionHeader icon={User} title={t('tradeIn.section1Title')} subtitle={t('tradeIn.section1Subtitle')} isDark={isDark} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <FormField label={t('financing.firstName')} required isDark={isDark}><Input value={personalInfo.firstName} onChange={(e) => updatePersonalField('firstName', e.target.value)} required className={inputClass} data-testid="first-name-input" /></FormField>
              <FormField label={t('financing.lastName')} required isDark={isDark}><Input value={personalInfo.lastName} onChange={(e) => updatePersonalField('lastName', e.target.value)} required className={inputClass} data-testid="last-name-input" /></FormField>
              <FormField label={t('contact.emailLabel')} required isDark={isDark}><Input type="email" value={personalInfo.email} onChange={(e) => updatePersonalField('email', e.target.value)} required className={inputClass} data-testid="email-input" /></FormField>
              <FormField label={t('financing.mobileNumber')} required isDark={isDark}><Input type="tel" value={personalInfo.mobileNumber} onChange={(e) => updatePersonalField('mobileNumber', e.target.value)} required placeholder="(708) 222-6100" className={inputClass} data-testid="mobile-input" /></FormField>
              <FormField label={t('financing.homeNumber')} isDark={isDark}><Input type="tel" value={personalInfo.homeNumber} onChange={(e) => updatePersonalField('homeNumber', e.target.value)} placeholder="(708) 222-6100" className={inputClass} data-testid="home-phone-input" /></FormField>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className={sectionClass}>
            <SectionHeader icon={Car} title={t('tradeIn.section2Title')} subtitle={t('tradeIn.section2Subtitle')} isDark={isDark} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <FormField label={t('financing.vin')} isDark={isDark}><Input value={vehicleInfo.vin} onChange={(e) => updateVehicleField('vin', e.target.value)} maxLength={17} placeholder={t('tradeIn.vinPlaceholder')} className={inputClass} data-testid="vin-input" /></FormField>
              <FormField label={t('financing.vehicleYear')} required isDark={isDark}><Input type="number" min="1990" max={VEHICLE_YEAR_MAX} value={vehicleInfo.year} onChange={(e) => updateVehicleField('year', e.target.value)} required placeholder={t('tradeIn.yearPlaceholder')} className={inputClass} data-testid="year-input" /></FormField>
              <FormField label={t('financing.vehicleMake')} required isDark={isDark}><Input value={vehicleInfo.make} onChange={(e) => updateVehicleField('make', e.target.value)} required placeholder={t('tradeIn.makePlaceholder')} className={inputClass} data-testid="make-input" /></FormField>
              <FormField label={t('financing.vehicleModel')} required isDark={isDark}><Input value={vehicleInfo.model} onChange={(e) => updateVehicleField('model', e.target.value)} required placeholder={t('tradeIn.modelPlaceholder')} className={inputClass} data-testid="model-input" /></FormField>
              <FormField label={t('financing.trim')} required isDark={isDark}><Input value={vehicleInfo.trim} onChange={(e) => updateVehicleField('trim', e.target.value)} required placeholder={t('tradeIn.trimPlaceholder')} className={inputClass} data-testid="trim-input" /></FormField>
              <FormField label={t('tradeIn.cylinders')} required isDark={isDark}>
                <Select value={vehicleInfo.cylinders} onValueChange={(v) => updateVehicleField('cylinders', v)}>
                  <SelectTrigger className={selectTriggerClass} data-testid="cylinders-select"><SelectValue placeholder={t('tradeIn.selectCylinders')} /></SelectTrigger>
                  <SelectContent className={selectContentClass}>{CYLINDER_OPTIONS.map((opt) => (<SelectItem key={opt} value={opt} className={selectItemClass}>{opt}</SelectItem>))}</SelectContent>
                </Select>
              </FormField>
              <FormField label={t('financing.mileage')} required isDark={isDark}><Input type="number" min="0" value={vehicleInfo.mileage} onChange={(e) => updateVehicleField('mileage', e.target.value)} required placeholder={t('tradeIn.mileagePlaceholder')} className={inputClass} data-testid="mileage-input" /></FormField>
              <FormField label={t('vehicleModal.transmission')} required isDark={isDark}>
                <Select value={vehicleInfo.transmission} onValueChange={(v) => updateVehicleField('transmission', v)}>
                  <SelectTrigger className={selectTriggerClass} data-testid="transmission-select"><SelectValue placeholder={t('tradeIn.selectTransmission')} /></SelectTrigger>
                  <SelectContent className={selectContentClass}>{TRANSMISSION_TYPES.map((type) => (<SelectItem key={type} value={type} className={selectItemClass}>{type}</SelectItem>))}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Colour" required isDark={isDark}><Input value={vehicleInfo.colour} onChange={(e) => updateVehicleField('colour', e.target.value)} required placeholder={t('tradeIn.colourPlaceholder')} className={inputClass} data-testid="colour-input" /></FormField>
              <FormField label={t('tradeIn.financed')} required isDark={isDark}>
                <Select value={vehicleInfo.financed} onValueChange={(v) => updateVehicleField('financed', v)}>
                  <SelectTrigger className={selectTriggerClass} data-testid="financed-select"><SelectValue placeholder={t('tradeIn.selectOption')} /></SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="Yes" className={selectItemClass}>{t('common.yes')}</SelectItem>
                    <SelectItem value="No" className={selectItemClass}>{t('common.no')}</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label={t('tradeIn.financedWith')} isDark={isDark}><Input value={vehicleInfo.financedWith} onChange={(e) => updateVehicleField('financedWith', e.target.value)} placeholder="Bank/Finance Company" className={inputClass} disabled={vehicleInfo.financed !== "Yes"} data-testid="financed-with-input" /></FormField>
              <FormField label={t('tradeIn.payOffAmount')} isDark={isDark}><Input type="number" min="0" value={vehicleInfo.payOffAmount} onChange={(e) => updateVehicleField('payOffAmount', e.target.value)} placeholder="$0" className={inputClass} disabled={vehicleInfo.financed !== "Yes"} data-testid="payoff-input" /></FormField>
              <FormField label={t('tradeIn.accidents')} required isDark={isDark}>
                <Select value={vehicleInfo.accidents} onValueChange={(v) => updateVehicleField('accidents', v)}>
                  <SelectTrigger className={selectTriggerClass} data-testid="accidents-select"><SelectValue placeholder={t('tradeIn.selectOption')} /></SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="None" className={selectItemClass}>None</SelectItem>
                    <SelectItem value="1" className={selectItemClass}>1</SelectItem>
                    <SelectItem value="2" className={selectItemClass}>2</SelectItem>
                    <SelectItem value="3+" className={selectItemClass}>3+</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className={sectionClass}>
            <SectionHeader icon={Settings} title={t('tradeIn.section3Title')} subtitle={t('tradeIn.section3Subtitle')} isDark={isDark} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {VEHICLE_FEATURES.map((feature) => {
                const isSelected = selectedFeatures.includes(feature);
                return (
                  <div key={feature}
                    className={`flex items-center gap-2 p-3 border cursor-pointer transition-all ${
                      isSelected
                        ? isDark ? "bg-blue-600/20 border-blue-500/50" : "bg-red-600/10 border-red-500/50"
                        : isDark ? "bg-black/30 border-white/10 hover:border-white/20" : "bg-gray-50 border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => toggleFeature(feature)}
                    data-testid={`feature-${feature.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
                    <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${
                      isSelected ? isDark ? "bg-blue-600 border-blue-600" : "bg-red-600 border-red-600" : isDark ? "border-white/30" : "border-gray-400"
                    }`}>
                      {isSelected && (<svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>)}
                    </div>
                    <span className={`text-sm select-none ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{translateFeature(feature)}</span>
                  </div>
                );
              })}
            </div>
            <p className={`text-sm mt-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {selectedFeatures.length} {selectedFeatures.length !== 1 ? t('tradeIn.featuresSelected') : t('tradeIn.featureSelected')}
            </p>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className={sectionClass}>
            <SectionHeader icon={MessageSquare} title={t('tradeIn.section4Title')} subtitle={t('tradeIn.section4Subtitle')} isDark={isDark} />
            <Textarea value={vehicleDescription} onChange={(e) => setVehicleDescription(e.target.value)} placeholder={t('tradeIn.descriptionPlaceholder')}
              className={`rounded-none min-h-[150px] resize-none ${isDark ? 'bg-black/50 border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`}
              data-testid="vehicle-description-input" />
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }} className={sectionClass}>
            <SectionHeader icon={FileCheck} title={t('tradeIn.consentTitle')} subtitle={t('tradeIn.consentSubtitle')} isDark={isDark} />
            <ConsentAgreement
              id="consent"
              checked={consent}
              onCheckedChange={setConsent}
              isDark={isDark}
              testId="consent-checkbox"
              checkboxClassName={`mt-1 ${isDark ? 'border-white/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600' : 'border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600'}`}
            />
          </motion.section>

          <BotProtection onToken={setCaptchaToken} isDark={isDark} />

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
            <Button type="submit" disabled={submitting || !canSubmit}
              className={`w-full text-white rounded-none py-6 font-semibold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-testid="submit-trade-in-btn">
              {submitting ? t('tradeIn.submitting') : t('tradeIn.submitBtn')}
            </Button>
            {fieldsReady && !consent && (<p className="text-gray-500 text-sm mt-3 text-center">{t('consent.formRequired')}</p>)}
          </motion.div>
        </form>
      </div>

      <Footer />
    </div>
  );
}
